import { v4 as uuid } from 'uuid';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';

import Breadcrumb from '@Services/Breadcrumb';
import ExportService from '@Services/ExportService';
import { SearchTypes, socketConfig } from '@Constants/CommonConstants';
import { RESULTS_TEXTS } from '@Constants/Results';
import store from '@Store';

import { tgitems, dpitems, citations, SORT_BY_SIMILARITY_FIELD } from '@Constants';
import Entries from '@Services/Entries';
import * as ResultsDataManage from '@Services/ResultsDataManage';
import searchStorage from '@Services/SearchStorage';
import Catalyst from '@Services/Catalyst';
import RelativeContextService from '@Containers/RelativeContext/RelativeContextNgService';
import {
  getFullTextDocumentId as hopintoGetFullTextDocumentId,
  processResultsItemsForFullTextDocumentId as hopintoProcessResultsItemsForFullTextDocumentId,
} from '@Containers/Hopinto/Services/FullTextResults';
import { showGlobalSpinner, hideGlobalSpinner } from '@Containers/GlobalSpinner/GlobalSpinnerActions';
import { loadLimitResults } from '@Containers/ResultsFilters/ResultsFiltersService';
import { open as openZeroResultDialog } from '@Containers/ResultsFilters/ResultsFiltersComponents/ZeroFilterResults/ZeroFilterResultsActions';
import ExportReopenService from '@Services/ExportReopenService';
import { setActiveResults } from '@Containers/Header/HeaderComponentActions';
import NewRelicService, { BROWSER_INTERACTIONS_NAMES } from '@Services/NewRelicService';
import NotifyDialogsService from '@Containers/Dialogs/NotifyDialogsService';
import { findSimilarityMollFromBreadcrumb } from '@Containers/SortingProperties/HOCs/withSortingProperties';
import {
  resetPatentFamilyClusteringState as resetPatentFamilyClusteringStateAction,
  processPatentFamilyGroups,
  initPatentFamilyGroups as initPatentFamilyGroupsAction,
} from '@Containers/PatentFamilyClustering/actions';
import {
  activeBreadCrumbTotalResult as activeBreadCrumbTotalResultSelector,
  activeBreadCrumbRecordCount as activeBreadCrumbRecordCountSelector,
} from '@Containers/Breadcrumbs/BreadcrumbsSelectors';
import { generateHeatmapPath, generateResultsPath } from '@AppRoutes/utils';
import history from '@AppRoutes/history';
import { getCombinedRouteParams } from '@AppRoutes/AppRoutesSelectors';
import { enrichParamsWithSemanticFlags } from '@Pages/Results/semanticSearchUtils';
import {
  INIT_RESULTS_STATE,
  SET_CHECKED_ITEMS,
  CLEAN_RESULTS_STATE,
  SET_RESULTS_PER_PAGE,
  FETCH_RESULTS_ITEMS,
  FETCH_RESULTS_ITEMS_SUCCESS,
  FETCH_RESULTS_ITEMS_FAILURE,
  ADD_RESULTS_CHUNK,
  SET_ALL_RESULTS_CHECKED,
  SET_ALL_RESULTS_UNCHECKED,
  SET_RESULT_CHECKED,
  SET_PRODUCT_CHECKED,
  SET_PRODUCTS_CHECKED_FOR_SUBSTANCE,
  genericExportType,
} from './constants';
import {
  resultsFinished as resultsFinishedSelector,
  resultsItems as resultsItemsSelector,
  normalizedCheckedItems as normalizedCheckedItemsSelector,
} from './selectors';
import { assertCheckedItems } from './utils';
import { trackShowAllPreparations, trackResultsCheckboxChecked } from './analytics';

let fetchResultsSubject;
let activeRelativeContextSearchId;

export const updateBreadcrumb = (params) => () => Breadcrumb.updateBreadcrumbs(params);

export const updateCheckedItemsInBreadcrumb =
  ({ databaseId }, checkedItems) =>
  () => {
    Breadcrumb.updateResultsStateForDb(databaseId, 'checkedItems', checkedItems);
  };

export const initResultsStateForParams = (params) => (dispatch) => {
  const { databaseId } = params;
  const checkedItems = Breadcrumb.getResultsStateForDb(databaseId, 'checkedItems');
  dispatch(updateBreadcrumb(params));
  dispatch({
    type: INIT_RESULTS_STATE,
    payload: { params, ...(assertCheckedItems(checkedItems) && { checkedItems }) },
  });
  dispatch(setActiveResults(true));
};

export const cleanResultsState = () => (dispatch, getState) => {
  dispatch({
    type: CLEAN_RESULTS_STATE,
  });

  if (fetchResultsSubject) {
    const resultsFinished = resultsFinishedSelector(getState());
    if (!resultsFinished) {
      fetchResultsSubject.unsubscribe();
    }
    fetchResultsSubject = null;
  }

  // Patent Family Clustering
  dispatch(resetPatentFamilyClusteringStateAction());

  if (activeRelativeContextSearchId) {
    RelativeContextService.cancelRelativeContextRequest(activeRelativeContextSearchId);
  }
};

export const setResultsPerPage = (resultsPerPage) => (dispatch) => {
  dispatch({
    type: SET_RESULTS_PER_PAGE,
    payload: resultsPerPage,
  });
};

let preventExportModal;
export const openExportDialog =
  (tpd, params, { resultSize, relativeContexts: relativeContextsImmutable }) =>
  (dispatch, getState) => {
    if (preventExportModal) return;
    preventExportModal = true;
    try {
      const normalizedCheckedItems = normalizedCheckedItemsSelector(getState());
      const selected = normalizedCheckedItems.map(({ index, absoluteIndex, id }) => {
        // Patent Family Clustering - use absoluteIndex
        if (absoluteIndex) {
          return { index: absoluteIndex, id };
        }

        // Generic results - use index
        return { index, id };
      });
      const { context } = params;
      const relativeContexts =
        relativeContextsImmutable && relativeContextsImmutable.toJS ? relativeContextsImmutable.toJS() : relativeContextsImmutable;
      const exportParams =
        context === tgitems || context === dpitems
          ? {
              ...params,
              resultSize: get(relativeContexts, ['substances', 'resultSize']),
              relativeContexts,
            }
          : {
              ...params,
            };
      ExportService.openResultExportDialog({
        exportType: tpd || genericExportType,
        parentParams: exportParams,
        entries: {
          total: exportParams.resultSize || resultSize,
          totalResult: activeBreadCrumbTotalResultSelector(getState()),
          recordCount: activeBreadCrumbRecordCountSelector(getState()),
          selected,
        },
        noFormats: !!tpd,
      });
      preventExportModal = false;
    } catch (e) {
      Logger.error(e);
    }
  };

export const checkReopenExport =
  (params, { relativeContexts }) =>
  (dispatch, getState) => {
    const resultSize = activeBreadCrumbTotalResultSelector(getState());

    const reopenExport = ExportReopenService.checkReopenExport();
    if (reopenExport) {
      // Check and set checkedItems in case of reopen export
      const checkedItems = Breadcrumb.getResultsStateForDb(params?.databaseId, 'checkedItems');
      if (assertCheckedItems(checkedItems)) {
        dispatch({
          type: SET_CHECKED_ITEMS,
          payload: { checkedItems },
        });
      }

      dispatch(
        openExportDialog(reopenExport !== genericExportType ? reopenExport : null, params, {
          resultSize,
          relativeContexts,
        })
      );
      ExportReopenService.saveReopenExport(null);
    }
  };

export const getRelativeContexts = (params) => async (dispatch) => {
  const { context, hitSetKey, resultName, databaseId } = params;
  try {
    activeRelativeContextSearchId = uuid();

    const relativeContexts = await RelativeContextService.getRelativeContexts(
      {
        context,
        hitSetKey,
        resultName,
        databaseId,
        searchId: activeRelativeContextSearchId,
      },
      history
    );

    if (relativeContexts) {
      const { hitSetKey: fullHitSetKey } = relativeContexts.find((relativeContext) => !!relativeContext.hitSetKey) || {};
      // Update active breadcrumb with full hitSetKey
      if (fullHitSetKey && fullHitSetKey !== hitSetKey) {
        dispatch(updateBreadcrumb({ ...params, hitSetKey: fullHitSetKey }));
      }
    }

    dispatch(checkReopenExport(params, { relativeContexts }));
  } catch (e) {
    if (e === socketConfig.REASON_STOP) {
      Logger.debug('Relative context request cancelled');
    } else {
      Logger.error(e);
    }
  } finally {
    activeRelativeContextSearchId = null;
  }
};

export const addResultsChunk = (items, params) => {
  let processedItems = items;
  const hopintoFullTextDocumentId = hopintoGetFullTextDocumentId();

  return (dispatch, getState) => {
    // hopinto full text - check results
    if (hopintoFullTextDocumentId) {
      hopintoProcessResultsItemsForFullTextDocumentId(items);
    }
    const resultsItems = resultsItemsSelector(getState());

    // Patent Family Clustering
    if (get(params, 'context') === citations) {
      processedItems = processPatentFamilyGroups(items);
      dispatch(initPatentFamilyGroupsAction(processedItems));
    }

    dispatch({
      type: ADD_RESULTS_CHUNK,
      payload: processedItems,
    });

    if (isEmpty(resultsItems)) {
      NewRelicService.endBrowserInteraction(BROWSER_INTERACTIONS_NAMES.TIME_TO_VIEW_SUBSTANCES);
      NewRelicService.endBrowserInteraction(BROWSER_INTERACTIONS_NAMES.TIME_TO_APPLY_FILTERS_TO_SUBSTANCES);
    }
  };
};

export const fetchResultsItems = (params, activeBreadcrumb) => (dispatch) => {
  const { facets, totalResult: resultSize } = activeBreadcrumb || {};

  const filterApplied = !isEmpty(facets);

  let similarityMol = null;
  if (params.sort && params.sort === SORT_BY_SIMILARITY_FIELD) {
    // additional param for similarity sorting
    similarityMol =
      findSimilarityMollFromBreadcrumb(activeBreadcrumb) ||
      findSimilarityMollFromBreadcrumb(Breadcrumb.retrieveRootBreadcrumb(params.databaseId));
  }

  dispatch(setResultsPerPage(Entries.limit));
  dispatch({ type: FETCH_RESULTS_ITEMS });

  const paramWithSemantic = enrichParamsWithSemanticFlags(params, activeBreadcrumb);

  fetchResultsSubject = Entries.getPage({ resultSize, filterApplied, similarityMol, ...paramWithSemantic });

  fetchResultsSubject.subscribe({
    next: (items) => {
      if (items) {
        dispatch(addResultsChunk(items, params));
      }
    },
    error: (error) => {
      dispatch({
        type: FETCH_RESULTS_ITEMS_FAILURE,
      });
      Logger.error(error);
    },
    complete: () => {
      dispatch({
        type: FETCH_RESULTS_ITEMS_SUCCESS,
      });
    },
  });
};

export const updateResultsPerPage =
  ({ resultsPerPage }, params, activeBreadcrumb) =>
  (dispatch, getState) => {
    const currentResultsPerPage = Entries.limit;

    if (!resultsPerPage || resultsPerPage === currentResultsPerPage) {
      return;
    }

    const resultsItems = resultsItemsSelector(getState());
    if (isEmpty(resultsItems)) {
      return;
    }

    const resultSize = activeBreadCrumbTotalResultSelector(getState());
    const { pageNumber: currentPageNumber } = params;
    const lastResultItem = resultsItems[resultsItems.length - 1];
    const lastIndex = Number(lastResultItem.index);
    if (!Number.isFinite(lastIndex)) {
      return;
    }
    const pageNumber = Math.ceil(lastIndex / resultsPerPage);

    Entries.setLimit(resultsPerPage);

    if (pageNumber === Number(currentPageNumber)) {
      dispatch(fetchResultsItems({ resultSize, ...params }, activeBreadcrumb));
    } else {
      const routeParams = getCombinedRouteParams(store.getState());

      const pathName = generateResultsPath({ ...routeParams, pageNumber });
      history.push({
        pathname: pathName,
        state: { ...routeParams, pageNumber },
      });
    }
  };

export const selectDatabase =
  ({
    context,
    databaseId,
    resultName,
    searchId,
    sort,
    order,
    hitSetKey,
    isFromAdvancedSearch,
    isHeatmapShown,
    heatmapResultName,
    sortedMatrixName = null,
    fromContext,
  }) =>
  () => {
    if (isHeatmapShown) {
      const params = {
        context: dpitems,
        databaseId,
        resultName: heatmapResultName,
        hitSetKey,
        searchId,
        isHeatmap: true,
        sortedMatrixName,
        fromContext,
      };
      const pathName = generateHeatmapPath(params);
      history.push({
        pathname: pathName,
        state: {
          context: dpitems,
          databaseId,
          resultName: heatmapResultName,
          searchId,
          sort,
          order,
          hitSetKey,
          isFromAdvancedSearch,
          isHeatmapShown,
          heatmapResultName,
          sortedMatrixName,
          fromContext,
        },
      });
    } else {
      const params = {
        context,
        databaseId,
        resultName,
        hitSetKey,
        searchId,
        pageNumber: 1,
        order,
        sort,
        isFromAdvancedSearch,
        fromContext,
      };
      const pathName = generateResultsPath(params);

      history.push({
        pathname: pathName,
        state: params,
      });
    }
  };

export const goToResultPage =
  ({ resultName, databaseId, context, hitSetKey, resultSize, recordCount }, { limitOrExclude }) =>
  async () => {
    const searchId = uuid();
    const breadcrumb = {
      resultName,
      databaseId,
      context,
      searchId,
      hitSetKey,
      limitOrExclude,
      recordCount,
    };

    try {
      await Breadcrumb.createBreadcrumb(breadcrumb, resultSize);
      const params = {
        context,
        databaseId,
        resultName,
        searchId,
        hitSetKey,
        limitOrExclude,
        pageNumber: 1,
        sort: '',
      };
      const pathName = generateResultsPath(params);

      history.push({
        pathname: pathName,
        state: params,
      });
    } catch (e) {
      Logger.error(e);
    }
  };

export const showAllPreparations = (params) => async (dispatch) => {
  const { resultName, databaseId, hitSetKey } = params;
  dispatch(showGlobalSpinner());
  trackShowAllPreparations();

  try {
    const resultData = await ResultsDataManage.getAllPreparations({
      resultName,
      databaseId,
      hitSetKey,
    });

    if (resultData.resultSize > 0) {
      dispatch(goToResultPage(resultData, params));
    } else {
      NotifyDialogsService.openModalWindow({
        text: RESULTS_TEXTS.zeroPreparationsText,
        confirmText: RESULTS_TEXTS.zeroPreparationsConfirmText,
        type: 'info',
        backdrop: 'static',
        trackingOptions: {
          component: 'Results list',
        },
      });
    }
  } catch (error) {
    Logger.error(error);
  }

  dispatch(hideGlobalSpinner());
};

/* Toolbar checkbox */
export const setAllResultsChecked = (checked) => ({
  type: SET_ALL_RESULTS_CHECKED,
  payload: { checked },
});

/* Toolbar checkbox */
export const setAllResultsUnchecked = () => ({
  type: SET_ALL_RESULTS_UNCHECKED,
});

/* Result checkbox */
export const setResultChecked = (item, checked, context) => {
  // AA
  if (checked) {
    trackResultsCheckboxChecked(item, context);
  }

  return {
    type: SET_RESULT_CHECKED,
    payload: { item, checked },
  };
};

/* Product checkbox */
export const setProductChecked = (product, checked) => ({
  type: SET_PRODUCT_CHECKED,
  payload: { product, checked },
});

export const setProductsCheckedForSubstance = ({ products, substanceIndex }) => ({
  type: SET_PRODUCTS_CHECKED_FOR_SUBSTANCE,
  payload: { products, substanceIndex },
});

export const limitTo =
  (option, { context, databaseId, resultName, hitSetKey, sort, order }, activeBreadcrumb) =>
  async (dispatch, getState) => {
    const { exclude } = option || {};
    const normalizedCheckedItems = normalizedCheckedItemsSelector(getState());
    const checkedIndexes = normalizedCheckedItems.map(({ index, relativeIndex }) => {
      // Patent Family Clustering - use relativeIndex ('1/1', '1/2', ...)
      if (relativeIndex) {
        return relativeIndex;
      }

      // Generic results - use index
      return index;
    });
    const checkedItemsWithProducts = normalizedCheckedItems.filter(({ products }) => !isEmpty(products));
    const selectedFacts = checkedItemsWithProducts.map(({ index: substanceIndex, products }) => {
      const productIndexes = products.map(({ productIndex }) => productIndex);
      productIndexes.sort((a, b) => a - b);

      return `${substanceIndex}:("PROD"(${productIndexes.join('-')}))`;
    });

    // Stop execution if checked entries are zero
    if (checkedIndexes.length === 0) {
      return;
    }

    let similarityMol = null;
    if (sort && sort === SORT_BY_SIMILARITY_FIELD) {
      // additional param for similarity sorting
      similarityMol =
        findSimilarityMollFromBreadcrumb(activeBreadcrumb) ||
        findSimilarityMollFromBreadcrumb(Breadcrumb.retrieveRootBreadcrumb(databaseId));
    }

    const requestParams = {
      context,
      databaseId,
      resultName,
      hitSetKey,
      sort,
      order,
      similarityMol,
      properties: [
        {
          indexes: checkedIndexes,
          selectedFacts: !isEmpty(selectedFacts) ? `{${selectedFacts.join(';')}}` : '',
        },
      ],
      ...(exclude && { exclude }),
    };
    const paramWithSemantic = enrichParamsWithSemanticFlags(requestParams, activeBreadcrumb);

    try {
      const response = await loadLimitResults(paramWithSemantic);
      const { resultSize } = response;

      if (!resultSize) {
        Catalyst.trackError({
          message: RESULTS_TEXTS.zeroFilterResults,
          component: 'Results list',
        });
        dispatch(openZeroResultDialog());
        return;
      }

      const searchId = await searchStorage.setNew({
        searchType: SearchTypes.MANUALLY_FILTERED,
      });

      const searchParams = {
        ...response,
        sort,
        order,
        searchId,
        resetBreadcrumbs: false,
        limitOrExclude: exclude ? 'exclude' : 'limit',
      };
      const searchParamsWithSemantic = enrichParamsWithSemanticFlags(searchParams, activeBreadcrumb);

      await Breadcrumb.createBreadcrumb(searchParamsWithSemantic, resultSize);
      ResultsDataManage.redirectToResults(searchParamsWithSemantic);
    } catch (e) {
      Logger.error(e);
    }
  };
